import { useCallback } from "react";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import type { AuftragLookup } from "@/lib/material";
import { isBookableForHours } from "@/lib/material";
import { toast } from "@/lib/toast";
import {
  AUFTRAG_AUTOCOMPLETE_EMPTY,
  AUFTRAG_AUTOCOMPLETE_PLACEHOLDER,
  loadAuftragAutocompleteOptions,
} from "@/lib/auftragAutocomplete";

interface ProjectAuftragAutocompleteProps {
  value: AutocompleteOption<AuftragLookup> | null;
  onChange: (next: AutocompleteOption<AuftragLookup> | null) => void;
  label?: string;
  required?: boolean;
  error?: string | null;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Standard true: gesperrte Projektstatus ausblenden / ablehnen. */
  forHourBooking?: boolean;
  /** Nachträgliche Stunden relativ zu diesem Mitarbeiter prüfen. */
  forUserLogin?: string | null;
}

export function ProjectAuftragAutocomplete({
  value,
  onChange,
  label,
  required,
  error,
  className,
  placeholder = AUFTRAG_AUTOCOMPLETE_PLACEHOLDER,
  disabled,
  forHourBooking = true,
  forUserLogin,
}: ProjectAuftragAutocompleteProps) {
  const loadOptions = useCallback(
    (query: string) =>
      loadAuftragAutocompleteOptions(query, { forHourBooking, forUserLogin }),
    [forHourBooking, forUserLogin],
  );

  const handleChange = useCallback(
    (next: AutocompleteOption<AuftragLookup> | null) => {
      if (next && forHourBooking && !isBookableForHours(next.value)) {
        toast.error(
          "Für Projekte mit Status Fertiggestellt, Abgerechnet oder Bezahlt können keine Stunden mehr erfasst oder verändert werden.",
        );
        return;
      }
      onChange(next);
    },
    [forHourBooking, onChange],
  );

  return (
    <LockedAutocomplete<AuftragLookup>
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      minChars={0}
      loadOptions={loadOptions}
      required={required}
      error={error || undefined}
      emptyText={AUFTRAG_AUTOCOMPLETE_EMPTY}
      className={className}
      disabled={disabled}
    />
  );
}
